import { CustomerStateMachine } from './CustomerStateMachine';
import type { CustomerArchetype, MenuItemId } from '../types';

const archetypeConfig = {
  STANDARD: { patience: 58, label: 'Misafir', color: 0xd96f4d, tipMultiplier: 1, decayMultiplier: 1 },
  PATIENT: { patience: 75, label: 'Sakin', color: 0x4f8f83, tipMultiplier: 1, decayMultiplier: 0.86 },
  GENEROUS: { patience: 62, label: 'Cömert', color: 0xc79235, tipMultiplier: 1.55, decayMultiplier: 1 },
  IMPATIENT: { patience: 49, label: 'Aceleci', color: 0x9f4f70, tipMultiplier: 1.1, decayMultiplier: 1.12 },
} as const;

export class Customer {
  readonly stateMachine = new CustomerStateMachine();
  readonly maxPatience: number;
  patience: number;
  tableId: number | null = null;
  decisionRemaining = 0;
  eatingRemaining = 0;
  readonly orders: MenuItemId[];

  constructor(
    public readonly id: number,
    public readonly archetype: CustomerArchetype,
    order: MenuItemId | MenuItemId[],
    patienceBonus = 0,
  ) {
    this.orders = Array.isArray(order) ? [...order] : [order];
    this.maxPatience = archetypeConfig[archetype].patience + patienceBonus;
    this.patience = this.maxPatience;
  }

  get order(): MenuItemId {
    return this.orders[0];
  }

  get config() {
    return archetypeConfig[this.archetype];
  }

  get patienceRatio(): number {
    return Math.max(0, this.patience / this.maxPatience);
  }

  tick(deltaSeconds: number): boolean {
    const waitingStates = [
      'WAITING_FOR_SEAT',
      'WAITING_TO_ORDER',
      'WAITING_FOR_FOOD',
      'WAITING_TO_PAY',
    ];
    if (waitingStates.includes(this.stateMachine.state)) {
      const pressure = this.stateMachine.state === 'WAITING_FOR_FOOD' ? 0.72 : 1;
      this.patience -= deltaSeconds * pressure * this.config.decayMultiplier;
    }
    return this.patience <= 0;
  }

  calculateTip(): number {
    const happiness = Math.max(0, this.patienceRatio);
    return Math.round((2 + happiness * 7) * this.config.tipMultiplier);
  }

  calculateSpeedBonus(orderValue: number): number {
    const speedRate = 0.05 + Math.max(0, Math.min(1, this.patienceRatio)) * 0.15;
    return Math.max(1, Math.round(orderValue * speedRate));
  }
}
